import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  COUNTRY_CALLING_CODES,
  digitsOnlyPhone,
} from '@/data/countryCallingCodes';
import { buildContactDuplicateLookupVariants } from '@/utils/contactUtils';
import * as XLSX from 'xlsx';
import { Trans, useTranslation } from "react-i18next";

/**
 * Residual risk (not addressed in this PR):
 * Brazilian legacy contacts stored without DDI can, in rare cases, share the same
 * digit sequence as an international E.164 number (e.g. BR national 51912345678 vs
 * Peru +51 912345678). Future migration/normalization will handle that separately.
 */

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface ImportedContact {
  nome: string;
  email: string;
  ddi: string;
  phone: string;
  tags: string;
  nome_do_agente?: string;
}

type ExistingContactRow = {
  id: string;
  jid: string;
  name: string | null;
  phone: string | null;
};

type PreparedImportRow = {
  nome: string;
  email: string | null;
  ddi: string;
  nationalPhone: string;
  fullPhone: string;
  primaryJid: string;
  phones: string[];
  jids: string[];
  tags: string[];
  assistantId: string | null;
};

const VALID_DDI_CODES = new Set(COUNTRY_CALLING_CODES.map((c) => c.code));
const LOOKUP_CHUNK_SIZE = 200;

/** Read Excel cells as text to avoid numeric coercion / scientific notation. */
function cellAsText(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value)) return String(value);
    return String(value);
  }
  return String(value).trim();
}

/** Contract: phone is always national (no DDI). fullPhone = ddi + national. */
function buildFullPhoneFromParts(ddi: string, nationalPhone: string): string {
  const cc = digitsOnlyPhone(ddi);
  const nat = digitsOnlyPhone(nationalPhone);
  if (!cc || !nat) return '';
  return `${cc}${nat}`;
}

function parseDdiAndNational(
  ddiRaw: string,
  phoneRaw: string
):
  | {
      ok: true;
      ddi: string;
      nationalPhone: string;
      fullPhone: string;
    }
  | {
      ok: false;
      field: 'ddi' | 'phone';
      message: string;
    } {
  const ddi = digitsOnlyPhone(ddiRaw);
  const nationalPhone = digitsOnlyPhone(phoneRaw);

  if (!ddi) {
    return { ok: false, field: 'ddi', message: 'DDI é obrigatório' };
  }
  if (!VALID_DDI_CODES.has(ddi)) {
    return {
      ok: false,
      field: 'ddi',
      message: `DDI "${ddiRaw}" inválido. Use um código da lista (ex: 55, 51, 1)`,
    };
  }
  if (!nationalPhone) {
    return { ok: false, field: 'phone', message: 'Telefone é obrigatório' };
  }
  if (nationalPhone.startsWith(ddi)) {
    return {
      ok: false,
      field: 'phone',
      message: 'Informe apenas o número nacional em phone (sem o DDI)',
    };
  }

  const fullPhone = buildFullPhoneFromParts(ddi, nationalPhone);
  if (fullPhone.length < 10) {
    return {
      ok: false,
      field: 'phone',
      message: 'Telefone inválido (número internacional com menos de 10 dígitos)',
    };
  }

  return { ok: true, ddi, nationalPhone, fullPhone };
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

const ImportarContatos: React.FC = () => {
  const { t } = useTranslation("crm");
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [previewData, setPreviewData] = useState<ImportedContact[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      alert(t("import.errWrongFile"));
      return;
    }

    setFile(selectedFile);
    setValidationErrors([]);
    setImportSuccess(false);
    setPreviewData([]);

    await validateFile(selectedFile);
  };

  const validateFile = async (fileToValidate: File) => {
    setValidating(true);
    const errors: ValidationError[] = [];
    const preview: ImportedContact[] = [];

    try {
      const data = await fileToValidate.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: true,
        defval: '',
      }) as unknown[][];

      if (jsonData.length < 2) {
        errors.push({
          row: 0,
          field: 'geral',
          message: 'Arquivo vazio ou sem dados',
        });
        setValidationErrors(errors);
        setValidating(false);
        return;
      }

      const headers = jsonData[0];
      const requiredHeaders = ['nome', 'email', 'ddi', 'phone', 'tags'];

      const normalizedHeaders = headers.map((h) => cellAsText(h).toLowerCase());

      for (const required of requiredHeaders) {
        if (!normalizedHeaders.includes(required)) {
          errors.push({
            row: 0,
            field: required,
            message: `Coluna "${required}" não encontrada no arquivo`,
          });
        }
      }

      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidating(false);
        return;
      }

      const nomeIndex = normalizedHeaders.indexOf('nome');
      const emailIndex = normalizedHeaders.indexOf('email');
      const ddiIndex = normalizedHeaders.indexOf('ddi');
      const phoneIndex = normalizedHeaders.indexOf('phone');
      const tagsIndex = normalizedHeaders.indexOf('tags');
      const nomeDoAgenteIndex = normalizedHeaders.indexOf('nome_do_agente');

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (!row || row.length === 0 || !row.some((cell) => cellAsText(cell))) {
          continue;
        }

        const nome = cellAsText(row[nomeIndex]);
        const email = cellAsText(row[emailIndex]);
        const ddiRaw = cellAsText(row[ddiIndex]);
        const phoneRaw = cellAsText(row[phoneIndex]);
        const tags = cellAsText(row[tagsIndex]);
        const nomeDoAgente =
          nomeDoAgenteIndex >= 0 ? cellAsText(row[nomeDoAgenteIndex]) : '';

        if (!nome) {
          errors.push({
            row: i + 1,
            field: 'nome',
            message: 'Nome é obrigatório',
          });
        }

        const parsed = parseDdiAndNational(ddiRaw, phoneRaw);
        if (!parsed.ok) {
          errors.push({
            row: i + 1,
            field: parsed.field,
            message: parsed.message,
          });
        }

        if (email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          errors.push({
            row: i + 1,
            field: 'email',
            message: 'E-mail inválido',
          });
        }

        if (errors.filter((e) => e.row === i + 1).length === 0 && parsed.ok) {
          preview.push({
            nome,
            email,
            ddi: parsed.ddi,
            phone: parsed.nationalPhone,
            tags,
            nome_do_agente: nomeDoAgente,
          });
        }
      }

      setValidationErrors(errors);
      setPreviewData(preview.slice(0, 5));
    } catch (error) {
      console.error('Erro ao ler arquivo:', error);
      errors.push({
        row: 0,
        field: 'geral',
        message: 'Erro ao processar arquivo. Verifique o formato.',
      });
      setValidationErrors(errors);
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!file || !user?.account_id || validationErrors.length > 0) {
      return;
    }

    setImporting(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: true,
        defval: '',
      }) as unknown[][];

      const headers = jsonData[0].map((h) => cellAsText(h).toLowerCase());
      const nomeIndex = headers.indexOf('nome');
      const emailIndex = headers.indexOf('email');
      const ddiIndex = headers.indexOf('ddi');
      const phoneIndex = headers.indexOf('phone');
      const tagsIndex = headers.indexOf('tags');
      const nomeDoAgenteIndex = headers.indexOf('nome_do_agente');

      if (!supabase) {
        alert(t("import.errDatabase"));
        setImporting(false);
        return;
      }

      const { data: assistantsData } = await supabase
        .from('account_assistants')
        .select('id, name')
        .eq('account_id', user.account_id);

      const assistantMap = new Map<string, string>();
      if (assistantsData) {
        for (const assistant of assistantsData) {
          const normalizedName = assistant.name.toLowerCase().trim();
          assistantMap.set(normalizedName, assistant.id);
        }
      }

      const allLookupPhones: string[] = [];
      const allLookupJids: string[] = [];
      const phoneToRowData = new Map<string, PreparedImportRow>();

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (!row || row.length === 0 || !row.some((cell) => cellAsText(cell))) {
          continue;
        }

        const nome = cellAsText(row[nomeIndex]);
        const email = cellAsText(row[emailIndex]) || null;
        const ddiRaw = cellAsText(row[ddiIndex]);
        const phoneRaw = cellAsText(row[phoneIndex]);
        const tagsStr = cellAsText(row[tagsIndex]);
        const nomeDoAgente =
          nomeDoAgenteIndex >= 0 ? cellAsText(row[nomeDoAgenteIndex]) : '';

        if (!nome) continue;

        const parsed = parseDdiAndNational(ddiRaw, phoneRaw);
        if (!parsed.ok) continue;

        const primaryJid = `${parsed.fullPhone}@s.whatsapp.net`;
        const { phones, jids } = buildContactDuplicateLookupVariants({
          jid: primaryJid,
          phone: parsed.fullPhone,
          is_group: false,
          countryCallingCode: parsed.ddi,
        });

        allLookupPhones.push(...phones);
        allLookupJids.push(...jids);

        const tagsList = tagsStr
          .split(',')
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0);

        let assistantId: string | null = null;
        if (nomeDoAgente) {
          const normalizedAgentName = nomeDoAgente.toLowerCase().trim();
          assistantId = assistantMap.get(normalizedAgentName) || null;
        }

        phoneToRowData.set(parsed.fullPhone, {
          nome,
          email,
          ddi: parsed.ddi,
          nationalPhone: parsed.nationalPhone,
          fullPhone: parsed.fullPhone,
          primaryJid,
          phones,
          jids,
          tags: tagsList,
          assistantId,
        });
      }

      if (phoneToRowData.size === 0) {
        alert(t("import.errNoValid"));
        setImporting(false);
        return;
      }

      const uniquePhones = [...new Set(allLookupPhones)];
      const uniqueJids = [...new Set(allLookupJids)];

      const existingByPhone = new Map<string, ExistingContactRow>();
      const existingByJid = new Map<string, ExistingContactRow>();

      const mergeExisting = (rows: ExistingContactRow[] | null | undefined) => {
        for (const contact of rows || []) {
          if (contact.phone) {
            const digits = digitsOnlyPhone(contact.phone);
            if (digits) existingByPhone.set(digits, contact);
          }
          if (contact.jid) existingByJid.set(contact.jid, contact);
        }
      };

      // Two separate bulk queries (OR semantics via merge). Do NOT chain .in() on one query.
      for (const chunk of chunkArray(uniquePhones, LOOKUP_CHUNK_SIZE)) {
        if (chunk.length === 0) continue;
        const { data: byPhone, error: phoneError } = await supabase
          .from('account_contacts')
          .select('id, jid, name, phone')
          .eq('account_id', user.account_id)
          .in('phone', chunk);
        if (phoneError) throw phoneError;
        mergeExisting(byPhone as ExistingContactRow[]);
      }

      for (const chunk of chunkArray(uniqueJids, LOOKUP_CHUNK_SIZE)) {
        if (chunk.length === 0) continue;
        const { data: byJid, error: jidError } = await supabase
          .from('account_contacts')
          .select('id, jid, name, phone')
          .eq('account_id', user.account_id)
          .in('jid', chunk);
        if (jidError) throw jidError;
        mergeExisting(byJid as ExistingContactRow[]);
      }

      const contactsToInsert: Record<string, unknown>[] = [];
      const contactsToUpdate: { id: string; [key: string]: unknown }[] = [];

      for (const rowData of phoneToRowData.values()) {
        let existingContact: ExistingContactRow | null = null;

        for (const p of rowData.phones) {
          const hit = existingByPhone.get(p);
          if (hit) {
            existingContact = hit;
            break;
          }
        }
        if (!existingContact) {
          for (const j of rowData.jids) {
            const hit = existingByJid.get(j);
            if (hit) {
              existingContact = hit;
              break;
            }
          }
        }

        const canAssignToOthers =
          user?.can_assign_contacts === true || user?.type === 'agency_user';
        const importedAttributionId =
          !canAssignToOthers && user?.auth_user_id ? user.auth_user_id : null;

        const contactData = {
          account_id: user.account_id,
          name: rowData.nome,
          email: rowData.email,
          phone: rowData.fullPhone,
          is_group: false,
          updated_at: new Date().toISOString(),
          tags: rowData.tags.length > 0 ? rowData.tags : null,
          account_assistant_id: rowData.assistantId || null,
          assistant_is_active: rowData.assistantId ? true : false,
        };

        if (existingContact) {
          // Never rewrite existing jid (preserves chat history).
          contactsToUpdate.push({
            id: existingContact.id,
            ...contactData,
          });
        } else {
          contactsToInsert.push({
            jid: rowData.primaryJid,
            created_at: new Date().toISOString(),
            last_message_at: new Date().toISOString(),
            ...contactData,
            ...(importedAttributionId
              ? { attribution_id: importedAttributionId }
              : {}),
          });
        }
      }

      let insertedCount = 0;
      let updatedCount = 0;

      if (contactsToInsert.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('account_contacts')
          .insert(contactsToInsert)
          .select('id');

        if (insertError) {
          console.error('Erro ao inserir contatos:', insertError);
          alert(t("import.errInsert", { message: insertError.message }));
          setImporting(false);
          return;
        }

        insertedCount = insertedData?.length || 0;
      }

      if (contactsToUpdate.length > 0) {
        for (const contact of contactsToUpdate) {
          const { id, ...updatePayload } = contact;
          const { error: updateError } = await supabase
            .from('account_contacts')
            .update(updatePayload)
            .eq('id', id);

          if (updateError) {
            console.error('Erro ao atualizar contato:', updateError);
          } else {
            updatedCount++;
          }
        }
      }

      const totalCount = insertedCount + updatedCount;

      setImportedCount(totalCount);
      setImportSuccess(true);
      setFile(null);
      setPreviewData([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro na importação:', error);
      alert(t("import.errImport"));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['nome', 'email', 'ddi', 'phone', 'tags', 'nome_do_agente'],
      ['João Silva', 'joao@example.com', '55', '11999999999', 'cliente, vip', 'Assistente Vendas'],
      ['Maria Santos', 'maria@example.com', '51', '912345678', 'lead', 'Suporte Técnico'],
      ['Alex US', 'alex@example.com', '1', '3055551234', 'cliente', ''],
      ['Carlos MX', '', '52', '5512345678', 'lead', ''],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);

    // Force ddi (col C) and phone (col D) as text to avoid Excel numeric coercion.
    for (let R = 1; R < template.length; R++) {
      for (const C of [2, 3]) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (!cell) continue;
        cell.t = 's';
        cell.v = String(cell.v);
        cell.z = '@';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contatos');

    ws['!cols'] = [
      { wch: 20 },
      { wch: 25 },
      { wch: 8 },
      { wch: 15 },
      { wch: 30 },
      { wch: 25 },
    ];

    XLSX.writeFile(wb, 'modelo_importacao_contatos.xlsx');
  };

  return (
    <div className="flex h-[calc(100dvh-120px)] w-full flex-col text-[#0F172A] dark:text-[#FAFAFA]">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[26px] font-semibold tracking-tight text-[#0F172A] sm:text-[30px] dark:text-[#FAFAFA]">
          {t("import.title")}
        </h2>
        <Button
          onClick={() => window.history.back()}
          variant="ghost"
          className="text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] dark:text-[#A1A1AA] dark:hover:bg-[#3F3F46] dark:hover:text-[#FAFAFA]"
        >
          <X className="mr-2 h-5 w-5" />
          {t("import.back")}
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-4xl space-y-6">
          {importSuccess && (
            <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-500/30 dark:bg-green-500/20">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-500" />
              <div>
                <h3 className="mb-1 font-semibold text-green-800 dark:text-green-300">
                  {t("import.done")}
                </h3>
                <p className="text-sm text-green-700 dark:text-green-200">
                  {t("import.successCount", { count: importedCount })}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="mb-4 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t("import.step1")}
            </h3>
            <p className="mb-4 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              {t("import.step1Hint")}
            </p>
            <Button
              onClick={downloadTemplate}
              className="bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
            >
              <Download className="mr-2 h-4 w-4" />
              {t("import.download")}
            </Button>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="mb-4 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t("import.step2")}
            </h3>
            <p className="mb-4 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              {t("import.step2Hint")}
            </p>

            <div className="rounded-xl border-2 border-dashed border-[#E5E7EB] p-8 text-center transition-colors hover:border-[#2563EB]/50 dark:border-[#3F3F46] dark:hover:border-[#52525B]">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex cursor-pointer flex-col items-center"
              >
                <FileSpreadsheet className="mb-3 h-12 w-12 text-[#94A3B8] dark:text-[#71717A]" />
                {file ? (
                  <div className="text-[#0F172A] dark:text-[#A1A1AA]">
                    <p className="font-semibold">{file.name}</p>
                    <p className="mt-1 text-sm text-[#64748B] dark:text-[#71717A]">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="mb-1 font-medium text-[#0F172A] dark:text-[#A1A1AA]">
                      {t("import.pickFile")}
                    </p>
                    <p className="text-sm text-[#64748B] dark:text-[#71717A]">
                      {t("import.dropHere")}
                    </p>
                  </>
                )}
              </label>
            </div>

            {validating && (
              <div className="mt-4 flex items-center justify-center gap-2 text-[#64748B] dark:text-[#A1A1AA]">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#64748B] border-t-transparent dark:border-[#A1A1AA]" />
                <span>{t("import.validating")}</span>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-500/30 dark:bg-red-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-500" />
                  <div className="flex-1">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">
                      {t("import.validationErrors", { count: validationErrors.length })}
                    </h4>
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                      {validationErrors.slice(0, 10).map((error, index) => (
                        <p key={index} className="text-sm text-red-700 dark:text-red-200">
                          {t("import.errorRow", { row: error.row, field: error.field, message: error.message })}
                        </p>
                      ))}
                      {validationErrors.length > 10 && (
                        <p className="text-sm italic text-red-700 dark:text-red-200">
                          {t("import.moreErrors", { count: validationErrors.length - 10 })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {previewData.length > 0 && validationErrors.length === 0 && (
              <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-500/30 dark:bg-green-500/10">
                <h4 className="mb-3 font-semibold text-green-800 dark:text-green-300">
                  {t("import.preview")}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-green-200 dark:border-[#3F3F46]">
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          {t("import.colName")}
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          {t("import.colEmail")}
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          {t("import.colDdi")}
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          {t("import.colPhone")}
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          {t("import.colTags")}
                        </th>
                        <th className="pb-2 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          {t("import.colAgent")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((contact, index) => (
                        <tr
                          key={index}
                          className="border-b border-green-100 last:border-0 dark:border-[#3F3F464D]"
                        >
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.nome}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.email || '-'}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.ddi}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.phone}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.tags || '-'}
                          </td>
                          <td className="py-2 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.nome_do_agente || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="mb-4 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t("import.step3")}
            </h3>
            <p className="mb-4 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              {t("import.step3Hint")}
            </p>
            <Button
              onClick={handleImport}
              disabled={!file || validationErrors.length > 0 || importing || validating}
              className="bg-green-600 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {importing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t("import.importing")}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t("import.title")}
                </>
              )}
            </Button>
          </div>

          <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-4 dark:border-blue-500/30 dark:bg-blue-500/10">
            <h4 className="mb-2 font-semibold text-[#1E40AF] dark:text-blue-300">
              {t("import.notesTitle")}
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-[#1E3A8A] dark:text-blue-200">
              <li>{t("import.noteFormat")}</li>
              <li>
                <Trans
                  ns="crm"
                  i18nKey="import.noteColumns"
                  values={{ ddi: "ddi" }}
                  components={{ b: <strong /> }}
                />
              </li>
              <li>
                <Trans
                  ns="crm"
                  i18nKey="import.noteDdi"
                  values={{ ddi: "ddi" }}
                  components={{ b: <strong /> }}
                />
              </li>
              <li>
                <Trans
                  ns="crm"
                  i18nKey="import.notePhone"
                  values={{ phone: "phone" }}
                  components={{ b: <strong /> }}
                />
              </li>
              <li>
                <Trans
                  ns="crm"
                  i18nKey="import.noteAgent"
                  values={{ agent: "nome_do_agente" }}
                  components={{ b: <strong /> }}
                />
              </li>
              <li>
                {t("import.noteAgentFound")}
              </li>
              <li>
                {t("import.noteAgentMissing")}
              </li>
              <li>{t("import.noteAgentCase")}</li>
              <li>{t("import.noteTags")}</li>
              <li>{t("import.noteDuplicates")}</li>
              <li>{t("import.noteDates")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarContatos;
